// A site's own SMTP server for its outgoing mail (/api/admin/sites/:id/email).
// The password is write-only: the API reports whether one is stored.

import { apiFetch, isApiError } from './api';

export type SmtpSecurity = 'tls' | 'starttls' | 'none';

export interface SiteEmailSettings {
  siteId: string;
  enabled: boolean;
  host: string;
  port: number;
  security: SmtpSecurity;
  username: string | null;
  hasPassword: boolean;
  fromEmail: string;
  fromName: string | null;
  replyTo: string | null;
  verifiedAt: string | null;
  lastError: string | null;
  updatedAt: string;
}

export interface SiteEmailInput {
  enabled: boolean;
  host: string;
  port: number;
  security: SmtpSecurity;
  username: string | null;
  /** null = keep the stored password. */
  password: string | null;
  fromEmail: string;
  fromName: string | null;
  replyTo: string | null;
}

export async function getSiteEmail(
  siteId: string,
): Promise<{ settings: SiteEmailSettings | null; encryptionAvailable: boolean }> {
  return apiFetch(`/api/admin/sites/${siteId}/email`);
}

export async function saveSiteEmail(siteId: string, input: SiteEmailInput): Promise<SiteEmailSettings> {
  const { settings } = await apiFetch<{ settings: SiteEmailSettings }>(`/api/admin/sites/${siteId}/email`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
  return settings;
}

export async function deleteSiteEmail(siteId: string): Promise<void> {
  await apiFetch(`/api/admin/sites/${siteId}/email`, { method: 'DELETE' });
}

/** Send a test with the given draft (or the saved settings when omitted). */
export async function testSiteEmail(siteId: string, to: string, draft?: SiteEmailInput): Promise<void> {
  await apiFetch(`/api/admin/sites/${siteId}/email/test`, {
    method: 'POST',
    body: JSON.stringify({ to, ...(draft ? { draft } : {}) }),
  });
}

/** The server's reason for a failed save or test. */
export function siteEmailError(err: unknown): string {
  if (!isApiError(err)) return 'The request failed. Check your connection and try again.';
  const body = (err.body ?? {}) as {
    message?: string;
    error?: string;
    issues?: { fieldErrors?: Record<string, string[]> };
  };
  if (body.message) return body.message;
  const field = body.issues?.fieldErrors && Object.entries(body.issues.fieldErrors)[0];
  if (field) return `${field[0]}: ${field[1][0]}`;
  if (body.error === 'rate-limited') return 'Too many tests in a minute. Wait a moment and try again.';
  return `The request failed (${err.status}).`;
}
